<template>
    <div class="relative flex flex-row">
        <a href="#admin-main" class="btn fixed top-2 left-2 z-60 -translate-y-16 btn-sm focus:translate-y-0">
            Skip to content
        </a>

        <AdminSideMenu :mobile-open="mobileMenuOpen"
                       @toggle-sidebar="sidebarOpen = !sidebarOpen"
                       @close="mobileMenuOpen = false" />

        <TransitionFade>
            <div v-if="mobileMenuOpen"
                 class="fixed inset-0 z-40 bg-black/20 md:hidden"
                 aria-hidden="true"
                 @click="mobileMenuOpen = false" />
        </TransitionFade>

        <div class="flex min-h-dvh min-w-0 grow flex-col">
            <div class="navbar sticky top-0 z-10 w-full gap-2 bg-base-200 px-3 transition-shadow md:px-6"
                 :class="{ 'shadow-lg': y }">
                <div class="flex-1">
                    <button type="button"
                            class="btn min-h-11 gap-2 btn-ghost px-3 md:hidden"
                            aria-controls="admin-menu"
                            :aria-expanded="mobileMenuOpen"
                            @click="mobileMenuOpen = !mobileMenuOpen">
                        <Icon name="mdi:menu" class="size-6" />
                        Menu
                    </button>
                </div>

                <span v-if="!online" role="status" class="badge badge-warning">
                    You’re offline
                </span>

                <button type="button"
                        class="btn min-h-11 gap-2 btn-ghost px-3"
                        aria-label="Add an ingredient"
                        @click="addIngredient">
                    <Icon name="mdi:plus" class="size-5" />
                    <span class="hidden sm:inline" aria-hidden="true">Ingredient</span>
                </button>

                <div class="dropdown dropdown-end">
                    <button type="button"
                            class="btn btn-circle btn-ghost"
                            :aria-label="`Account menu for ${userName}`">
                        <span class="grid size-10 place-items-center rounded-full bg-base-300 font-semibold
                                     text-base-content">
                            {{ userName.at(0) }}
                        </span>
                    </button>
                    <ul class="menu dropdown-content z-10 mt-2 w-48 rounded-box border border-base-300 bg-base-100
                               p-2 shadow-xl">
                        <li>
                            <NuxtLink :to="{ name: 'admin-settings' }" class="min-h-11 gap-2 no-underline">
                                <Icon name="mdi:cog-outline" class="size-5" />
                                Settings
                            </NuxtLink>
                        </li>
                        <li>
                            <button type="button" class="min-h-11 gap-2" @click="logout">
                                <Icon name="mdi:logout" class="size-5" />
                                Log out
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            <main id="admin-main" tabindex="-1" class="container py-6 focus:outline-none md:px-12">
                <slot />
            </main>
        </div>

        <AdminIngredientEditor ref="ingredientEditor" />
    </div>
</template>

<script setup lang="ts">
import type { Settings } from 'vue-toastify';
import type { IngredientAttributes, IngredientEditorMethods, PackagingAttributes, UnitAttributes } from '~/types';
import { ingredientEditorKey } from '~/fixtures';

const api = useApi();

defineOptions({
    name: 'Admin'
});

const nuxt = useNuxtApp();
const ingredientEditor = ref<IngredientEditorMethods>();
const ingredientStore = useIngredientStore();
const unitStore = useUnitStore();
const packagingStore = usePackagingStore();
const productStore = useProductStore();
const productTypes = useProductTypes();
const authStore = useAuthStore();
const loader = useLoadingIndicator();
const userName = computed(() => authStore.user?.name ?? '');
const sidebarOpen = useLocalStorage<boolean>('sidebarOpen', false, { initOnMounted: true });
const mobileMenuOpen = ref(false);

onUnmounted(useRouter().afterEach(() => mobileMenuOpen.value = false));
onKeyStroke('Escape', () => mobileMenuOpen.value = false);

provide(ingredientEditorKey, ingredientEditor);
provide('sidebarOpen', sidebarOpen);

onNuxtReady(async () => {
    if (!document.getElementById(Symbol('vue-toastify').toString())) {
        const plugin = await import('vue-toastify').then(exports => exports.default);

        nuxt.vueApp.use<Settings>(plugin, {
            theme: 'light',
            errorDuration: 3000,
            successDuration: 1500,
            pauseOnHover: true
        });
        void Promise.all([
            import('vue-toastify/index.css'),
            import('vue-toastify/themes/light.css')
        ]);
    }
});

const logout = async () => {
    loader.start();

    return authStore.logout()
        .then(async () => {
            await navigateTo({ path: '/' });
            // refresh the product store to get rid of the admin-specific data
            getAllProducts(api).then(products => {
                productStore.products = products;
            });
        })
        .catch(async () => navigateTo({ path: '/login' }))
        .finally(() => {
            // reset admin only values
            unitStore.units = [];
            packagingStore.packagings = [];
            productTypes.value = [];
            ingredientStore.ingredients = [];
            loader.finish();
        });
};

const addIngredient = () => {
    ingredientEditor.value!.openFresh();
};
const online = useOnline();
const { y } = useWindowScroll();

await Promise.all([
    api<{ productTypes: ProductType[] }>('/constants').then(data => {
        productTypes.value = data.productTypes;
        return;
    }),
    api<UnitAttributes[]>('/units').then(data => {
        unitStore.units = data;
        return;
    }),
    api<PackagingAttributes[]>('/packagings').then(data => {
        packagingStore.packagings = data;
        return;
    }),
    api<IngredientAttributes[]>('/ingredients?with[0]=products_count')
        .then(ingredients => ingredientStore.ingredients = ingredients)
]);
</script>
